import { Route } from '@/Enums/Routes/Route.enum';
import { NEW_PASSWORD_RULES } from '@/utils/validationRules';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { Button, CircularProgress, FormLabel, IconButton, InputAdornment, TextField } from '@mui/material';
import { HttpStatusCode } from 'axios';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getEmail, resetPassword } from './data';

const FormView = ({ token, setPasswordAccepted }) => {
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfrimNewPassword, setShowConfrimNewPassword] = useState(false);

  const [loading, setLoading] = useState(true);
  const [goToLogin, setGoToLogin] = useState(false);

  const handleClickShowNewPassword = () => setShowNewPassword(show => !show);
  const handleClickShowConfirmNewPassword = () => setShowConfrimNewPassword(show => !show);
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      email: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onSubmit = async data => {
    setLoading(true);
    try {
      await resetPassword(token, data.newPassword);
      setLoading(false);
      setPasswordAccepted(true);
    } catch (error) {
      console.error(error);
      toast.error('Something went wrong');
      setLoading(false);
    }
    return;
  };

  useEffect(() => {
    if (!token) return;

    const fetchEmail = async () => {
      const response = await getEmail(token);
      setValue('email', response.data.email);
      setLoading(false);
    };

    fetchEmail().catch(error => {
      if (error?.response?.status !== HttpStatusCode.NotFound) {
        console.error(error);
      }
      setGoToLogin(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token || goToLogin) {
    return <Navigate to={Route.LOGIN} />;
  }

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <>
      <h1 className='font-bold text-[32px] text-[#121212] mb-[4px]'>Set New Password</h1>
      <div className='text-[#84818A] text-[14px] font-medium mb-[32px]'>
        Enter new password for your account <span className='text-[#1B4332]'>{watch('email')}</span>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLabel htmlFor='new-password-input'>New Password</FormLabel>
        <Controller
          name='newPassword'
          control={control}
          rules={NEW_PASSWORD_RULES}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id='new-password-input'
              type={showNewPassword ? 'text' : 'password'}
              autoComplete='new-password'
              fullWidth
              error={!!error}
              helperText={error?.message}
              sx={{
                marginBottom: '16px',
              }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle new password visibility'
                        onClick={handleClickShowNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge='end'
                      >
                        {showNewPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },

                htmlInput: { placeholder: 'Enter New Password' },
              }}
            />
          )}
        />
        <FormLabel htmlFor='confirm-new-password-input'>Confirm New Password</FormLabel>
        <Controller
          name='confirmNewPassword'
          control={control}
          rules={{
            required: {
              value: true,
              message: 'Please confirm new password.',
            },
            validate: (value, formValues) => value === formValues.newPassword || "Passwords don't match",
          }}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id='confirm-new-password-input'
              sx={{
                marginBottom: '30px',
              }}
              fullWidth
              error={!!error}
              helperText={error?.message}
              type={showConfrimNewPassword ? 'text' : 'password'}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle confirm new password visibility'
                        onClick={handleClickShowConfirmNewPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge='end'
                      >
                        {showConfrimNewPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },

                htmlInput: { placeholder: 'Confirm new password' },
              }}
            />
          )}
        />
        <Button
          sx={{
            height: 48,
            fontWeight: 700,
            marginBottom: '16px',
          }}
          fullWidth
          variant='contained'
          type='submit'
        >
          Save Password
        </Button>
        <Link to='/'>
          <Button
            sx={{
              height: 48,
              fontWeight: 700,
            }}
            type='reset'
            variant='outlined'
            fullWidth
          >
            Go back to Login
          </Button>
        </Link>
      </form>
    </>
  );
};

FormView.propTypes = {
  token: PropTypes.string,
  setPasswordAccepted: PropTypes.func,
};

export default FormView;
